// Iter 108v — TicketDetail extracted from Helpdesk.jsx.
//
// All sub-components used exclusively by TicketDetail (EventLine,
// MessageBubble, AttachmentPreview, CollapsedNoteGroup, SlaPane,
// AiTriagePanel, TriageChip, OrgChainPane, FieldRow) plus the
// timeline builders live in this file, since they never render
// outside of a detail view. Shared utilities (fmt, slaCountdown,
// COLUMNS, PRIORITY_TINT, SLA_TONE, fmtStamp) live in helpdeskUtils.js
// and are imported.

import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, StickyNote, Send, Sparkles, Loader2, Paperclip, FileText, Download, X } from "lucide-react";
import { fmt, slaCountdown, fmtStamp, COLUMNS, PRIORITIES, SLA_TONE } from "./helpdeskUtils";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";

export function TicketDetail({ ticketId, agents, teams, canAgent, onRefresh, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 108g — Draft persistence per ticket. Restore a saved draft on
  // open so a page refresh (or accidental tab close) never eats a
  // half-written reply. Cleared once the message is sent.
  const draftKey = `helpdesk:draft:${ticketId}`;
  const draftKindKey = `helpdesk:draft-kind:${ticketId}`;
  const [reply, setReply] = useState(() => {
    try { return localStorage.getItem(draftKey) || ""; }
    catch { return ""; }
  });
  const [replyKind, setReplyKind] = useState(() => {
    try { return localStorage.getItem(draftKindKey) || "out"; }
    catch { return "out"; }
  });
  useEffect(() => {
    // Debounce localStorage writes so a burst of keystrokes doesn't
    // hammer sync IO on slow devices with many tabs open.
    const t = setTimeout(() => {
      try {
        if (reply) localStorage.setItem(draftKey, reply);
        else       localStorage.removeItem(draftKey);
      } catch { /* private-mode Safari, quota — silently skip */ }
    }, 300);
    return () => clearTimeout(t);
  }, [reply, draftKey]);
  useEffect(() => {
    try { localStorage.setItem(draftKindKey, replyKind); }
    catch { /* ignore */ }
  }, [replyKind, draftKindKey]);
  const [timelineSearch, setTimelineSearch] = useState("");
  const replyRef = useRef(null);
  const timelineRef = useRef(null);
  // Files staged for upload with the next reply. Declared up here
  // (before any conditional return) so hook order stays stable.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/helpdesk/tickets/${ticketId}`);
      setData(data);
    } catch { onNavigate("/helpdesk"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [ticketId]);

  usePageBreadcrumb(useMemo(() => {
    const t = data?.ticket;
    if (!t) return null;
    return [
      { label: "Helpdesk", to: "/helpdesk" },
      { label: t.cf_id ? `${t.cf_id} · ${t.subject || "Ticket"}` : (t.subject || "Ticket") },
    ];
  }, [data]));

  useEffect(() => {
    if (loading || !data) return;
    // Give the DOM one paint tick to lay out the timeline before we
    // snap it to the top and focus the composer.
    const t = setTimeout(() => {
      if (timelineRef.current) timelineRef.current.scrollTop = 0;
      if (replyRef.current) {
        try { replyRef.current.focus({ preventScroll: true }); }
        catch { replyRef.current.focus(); }
      }
    }, 50);
    return () => clearTimeout(t);
  }, [loading, data]);

  if (loading || !data) return <div className="p-8 text-slate-400">Loading ticket…</div>;
  const { ticket, messages } = data;

  const patch = async (patchBody) => {
    try {
      await api.patch(`/helpdesk/tickets/${ticketId}`, patchBody);
      await load();
      onRefresh?.();
    } catch { toast.error("Update failed"); }
  };
  const send = async () => {
    if (!reply.trim() && pendingFiles.length === 0) return;
    try {
      let msgId = null;
      if (reply.trim()) {
        const { data: msg } = await api.post(`/helpdesk/tickets/${ticketId}/messages`, {
          body_text: reply, direction: replyKind,
        });
        msgId = msg?.msg_id || null;
      }
      // Upload each pending attachment sequentially so a partial
      // failure gives the user a clear error rather than a race.
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f);
        if (msgId) fd.append("message_id", msgId);
        try {
          await api.post(`/helpdesk/tickets/${ticketId}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e) {
          toast.error(`Failed to upload ${f.name}: ${e.response?.data?.detail || "storage error"}`);
        }
      }
      setReply("");
      setPendingFiles([]);
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      await load();
      onRefresh?.();
    } catch { toast.error("Send failed"); }
  };

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    const MAX = 25 * 1024 * 1024;
    const accepted = arr.filter((f) => {
      if (f.size > MAX) { toast.error(`${f.name}: exceeds 25 MB`); return false; }
      return true;
    });
    setPendingFiles((cur) => [...cur, ...accepted]);
  };
  const dropHandler = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-12 divide-x divide-slate-200 bg-white">
      {/* Left: case details */}
      <aside className="col-span-3 overflow-y-auto p-4">
        <div className="mb-1 font-mono text-[10px] text-slate-400">{ticket.cf_id}</div>
        <h2 className="mb-4 text-lg font-semibold text-slate-800">{ticket.subject}</h2>
        <div className="space-y-3 text-xs">
          <FieldRow label="Status">
            <Select value={ticket.status} onValueChange={(v) => patch({ status: v })} disabled={!canAgent}>
              <SelectTrigger className="h-8 text-xs" data-testid="td-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Priority">
            <Select value={ticket.priority} onValueChange={(v) => patch({ priority: v })} disabled={!canAgent}>
              <SelectTrigger className="h-8 text-xs" data-testid="td-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Assignee">
            <Select value={ticket.assignee_user_id || "__none__"} onValueChange={(v) => patch({ assignee_user_id: v === "__none__" ? "" : v })} disabled={!canAgent}>
              <SelectTrigger className="h-8 text-xs" data-testid="td-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {agents.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Team">
            <Select value={ticket.team || "__none__"} onValueChange={(v) => patch({ team: v === "__none__" ? "" : v })} disabled={!canAgent}>
              <SelectTrigger className="h-8 text-xs" data-testid="td-team"><SelectValue placeholder="No team" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No team</SelectItem>
                {(teams || []).map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Requester"><span>{ticket.requester_name || ticket.requester_email}</span></FieldRow>
          <FieldRow label="Created"><span>{fmt(ticket.created_at)}</span></FieldRow>
          <FieldRow label="Updated"><span>{fmt(ticket.updated_at)}</span></FieldRow>
          <SlaPane ticket={ticket} />
          {data.org_chain && (data.org_chain.org || data.org_chain.partner) && (
            <OrgChainPane chain={data.org_chain} />
          )}
        </div>
      </aside>

      {/* Middle: conversation */}
      <section className="col-span-6 flex min-h-0 flex-col">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-1.5">
          <Input value={timelineSearch} onChange={(e) => setTimelineSearch(e.target.value)}
                 placeholder="Search this ticket's messages and events…"
                 className="h-7 text-xs"
                 data-testid="td-timeline-search" />
        </div>
        <div ref={timelineRef}
             className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
             data-testid="td-timeline">
          {(() => {
            const rawItems = buildTimeline(ticket, messages, data.events);
            const q = timelineSearch.trim().toLowerCase();
            const filtered = q ? rawItems.filter((it) => {
              if (it.kind === "msg") {
                const m = it.data;
                return (m.body_text || "").toLowerCase().includes(q)
                    || (m.author_name || "").toLowerCase().includes(q);
              }
              const e = it.data;
              return (e.kind || "").toLowerCase().includes(q)
                  || (e.actor_name || "").toLowerCase().includes(q)
                  || JSON.stringify(e.payload || {}).toLowerCase().includes(q);
            }) : rawItems;
            if (filtered.length === 0 && q) {
              return <div className="rounded border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400"
                          data-testid="td-timeline-empty">No matches for &ldquo;{timelineSearch}&rdquo;</div>;
            }
            const items = q ? filtered : collapseInternalNoteGroups(filtered);
            const orphanAtts = (data.attachments || []).filter((a) => !a.message_id);
            return (
              <>
                {orphanAtts.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs"
                       data-testid="td-orphan-attachments">
                    <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-slate-500">
                      <Paperclip className="h-3 w-3" /> Attachments
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {orphanAtts.map((a) => <AttachmentPreview key={a.att_id} a={a} />)}
                    </div>
                  </div>
                )}
                {items.map((item) => {
                  if (item.kind === "msg")        return <MessageBubble key={`m-${item.id}`} msg={item.data} attachments={data.attachments} />;
                  if (item.kind === "note-group") return <CollapsedNoteGroup key={item.id} hidden={item.data.hidden} attachments={data.attachments} />;
                  return <EventLine key={`e-${item.id}`} ev={item.data} />;
                })}
              </>
            );
          })()}
        </div>
        <div className="border-t border-slate-200 p-3" data-testid="td-reply-composer"
             onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
             onDragLeave={() => setDragOver(false)}
             onDrop={dropHandler}>
          <div className="mb-2 flex items-center gap-2">
            <Button size="sm" variant={replyKind === "out" ? "default" : "outline"} onClick={() => setReplyKind("out")}>
              <Send className="h-3.5 w-3.5" /> Reply
            </Button>
            {canAgent && (
              <Button size="sm" variant={replyKind === "internal" ? "default" : "outline"} onClick={() => setReplyKind("internal")}
                      data-testid="td-reply-internal">
                <StickyNote className="h-3.5 w-3.5" /> Internal note
              </Button>
            )}
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                   data-testid="td-attach-btn">
              <Paperclip className="h-3.5 w-3.5" /> Attach
              <input type="file" multiple hidden
                     onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                     data-testid="td-attach-input" />
            </label>
          </div>
          <Textarea ref={replyRef}
                    value={reply} onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    className={dragOver ? "border-2 border-dashed border-sky-400 bg-sky-50/40" : ""}
                    placeholder={dragOver
                      ? "Drop files to attach…"
                      : replyKind === "internal"
                        ? "Private note — not visible to requester"
                        : "Reply to requester…  (drag files here to attach)"}
                    data-testid="td-reply-body" />
          {pendingFiles.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="td-pending-files">
              {pendingFiles.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
                  <Paperclip className="h-3 w-3" />
                  {f.name}
                  <span className="text-slate-400">· {(f.size / 1024).toFixed(0)} KB</span>
                  <button type="button"
                          onClick={() => setPendingFiles((cur) => cur.filter((_, idx) => idx !== i))}
                          className="ml-1 text-slate-400 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={send}
                    disabled={!reply.trim() && pendingFiles.length === 0}
                    data-testid="td-reply-send">
              Send{pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ""}
            </Button>
          </div>
        </div>
      </section>

      {/* Right: AI panel only — activity events are now inline in the
          conversation timeline so agents can see what happened without
          hunting a separate sidebar. */}
      <aside className="col-span-3 overflow-y-auto bg-slate-50 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">AI suggestions</h3>
        {canAgent
          ? <AiTriagePanel ticket={ticket}
                           onReplyDraft={(draft) => { setReplyKind("out"); setReply(draft); }} />
          : <Card className="mb-4 border-dashed border-slate-300 bg-white p-3 text-xs text-slate-500">
              <p>AI triage is only available to agents.</p>
            </Card>}
      </aside>
    </div>
  );
}

// -------------------------------------------------------------------
// Timeline helpers
// -------------------------------------------------------------------

// Merge messages + events into a single timeline sorted by created_at.
// Filters out `message_*` events (redundant) and `ticket_created`
// (the original body already appears as message 0). Newest-first.
function buildTimeline(ticket, messages, events) {
  const items = [];
  if (ticket.body_text) {
    items.push({
      kind: "msg",
      id:   "initial",
      at:   ticket.created_at,
      data: {
        author_name: ticket.requester_name,
        direction:   "in",
        body_text:   ticket.body_text,
        created_at:  ticket.created_at,
      },
    });
  }
  (messages || []).forEach((m) => {
    items.push({ kind: "msg", id: m.msg_id, at: m.created_at, data: m });
  });
  (events || []).forEach((e) => {
    if (!e || !e.kind) return;
    if (e.kind === "ticket_created") return;
    if (e.kind && e.kind.startsWith("message_")) return;
    items.push({ kind: "ev", id: e.event_id, at: e.created_at, data: e });
  });
  items.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  return items;
}

// When a ticket has more than 3 consecutive internal notes, collapse
// the older ones behind a "Show N more notes" toggle so the timeline
// doesn't drown in agent chatter. Newest-first, so the freshest 3
// stay visible and older ones fold.
const INTERNAL_NOTE_VISIBLE_LIMIT = 3;

function collapseInternalNoteGroups(items) {
  const out = [];
  let run = [];
  const flushRun = () => {
    if (run.length === 0) return;
    if (run.length <= INTERNAL_NOTE_VISIBLE_LIMIT) {
      out.push(...run);
    } else {
      const visible = run.slice(0, INTERNAL_NOTE_VISIBLE_LIMIT);
      const hidden  = run.slice(INTERNAL_NOTE_VISIBLE_LIMIT);
      out.push(...visible);
      out.push({
        kind: "note-group",
        id:   `notegrp-${visible[visible.length - 1].id}`,
        at:   hidden[0].at,
        data: { hidden },
      });
    }
    run = [];
  };
  for (const it of items) {
    const isNote = it.kind === "msg" && it.data && it.data.direction === "internal";
    if (isNote) { run.push(it); }
    else        { flushRun(); out.push(it); }
  }
  flushRun();
  return out;
}

// -------------------------------------------------------------------
// Timeline pieces
// -------------------------------------------------------------------

function EventLine({ ev }) {
  const kind    = ev.kind || "";
  const payload = ev.payload || {};
  const desc = (() => {
    if (kind === "status_changed")   return <>changed status <b>{payload.from}</b> → <b>{payload.to}</b></>;
    if (kind === "priority_changed") return <>changed priority <b>{payload.from}</b> → <b>{payload.to}</b></>;
    if (kind === "team_changed")     return <>reassigned team <b>{payload.from || "—"}</b> → <b>{payload.to || "—"}</b></>;
    if (kind === "assigned") {
      if (!payload.to)   return <>unassigned agent</>;
      if (!payload.from) return <>assigned agent</>;
      return <>reassigned agent</>;
    }
    return <span className="text-slate-600">{kind.replace(/_/g, " ")}</span>;
  })();
  return (
    <div className="flex items-center gap-2 py-1 text-[11px] text-slate-500"
         data-testid={`td-event-${ev.event_id}`}>
      <Clock className="h-3 w-3 shrink-0 text-slate-400" />
      <span className="font-mono text-[10px] text-slate-400">{fmtStamp(ev.created_at)}</span>
      <span className="text-slate-400">·</span>
      <span className="font-medium text-slate-600">{ev.actor_name || "System"}</span>
      <span className="text-slate-400">·</span>
      <span>{desc}</span>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] font-semibold uppercase text-slate-400">{label}</div>
      {children}
    </div>
  );
}

function MessageBubble({ msg, attachments }) {
  const isNote = msg.direction === "internal";
  const isOut  = msg.direction === "out";
  const mine   = (attachments || []).filter((a) => a.message_id === msg.msg_id);
  return (
    <div className={`rounded-lg border p-3 text-xs ${isNote ? "border-amber-200 bg-amber-50" : isOut ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-white"}`}
         data-testid={`td-msg-${msg.msg_id || "initial"}`}>
      <div className="mb-1 flex items-center justify-between text-[10px] text-slate-500">
        <span className="font-medium text-slate-700">
          {msg.author_name} {isNote ? "· internal note" : isOut ? "· reply" : "· incoming"}
        </span>
        <span>{fmt(msg.created_at)}</span>
      </div>
      {msg.body_text && (
        <div className="whitespace-pre-wrap text-sm text-slate-800">{msg.body_text}</div>
      )}
      {mine.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`td-msg-attachments-${msg.msg_id || "initial"}`}>
          {mine.map((a) => <AttachmentPreview key={a.att_id} a={a} />)}
        </div>
      )}
    </div>
  );
}

// Inline preview for a helpdesk attachment. Renders images as
// thumbnails, video as a small player, everything else as a labelled
// download chip. Cookies auth the /api/helpdesk/attachments endpoint
// so <img>/<video> tags just work.
function AttachmentPreview({ a }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/helpdesk/attachments/${a.att_id}`;
  const ct  = a.content_type || "";
  const sizeLabel = a.size < 1024 * 1024
    ? `${(a.size / 1024).toFixed(0)} KB`
    : `${(a.size / (1024 * 1024)).toFixed(1)} MB`;
  if (ct.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" title={a.filename}
         className="block overflow-hidden rounded border border-slate-200 hover:border-sky-400"
         data-testid={`td-attach-${a.att_id}`}>
        <img src={url} alt={a.filename}
             className="h-32 w-32 object-cover" loading="lazy" />
      </a>
    );
  }
  if (ct.startsWith("video/")) {
    return (
      <video controls preload="metadata"
             className="h-32 rounded border border-slate-200 bg-black"
             data-testid={`td-attach-${a.att_id}`}>
        <source src={url} type={ct} />
      </video>
    );
  }
  const Icon = ct === "application/pdf" ? FileText : Paperclip;
  return (
    <a href={url} target="_blank" rel="noreferrer" title={a.filename} download={a.filename}
       className="inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:border-sky-400 hover:text-sky-800"
       data-testid={`td-attach-${a.att_id}`}>
      <Icon className="h-4 w-4" />
      <span className="max-w-[180px] truncate">{a.filename}</span>
      <span className="text-slate-400">{sizeLabel}</span>
      <Download className="h-3 w-3 text-slate-400" />
    </a>
  );
}

// Fold older internal notes into a single row that expands on click.
// Only appears when a run of internal notes exceeds
// INTERNAL_NOTE_VISIBLE_LIMIT.
function CollapsedNoteGroup({ hidden, attachments }) {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <div className="space-y-3">
        <button type="button"
                onClick={() => setOpen(false)}
                className="w-full rounded border border-dashed border-amber-300 bg-amber-50/60 px-3 py-1.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
                data-testid="td-notegroup-collapse">
          Hide {hidden.length} older internal note{hidden.length === 1 ? "" : "s"}
        </button>
        {hidden.map((it) => (
          <MessageBubble key={`m-${it.id}`} msg={it.data} attachments={attachments} />
        ))}
      </div>
    );
  }
  return (
    <button type="button"
            onClick={() => setOpen(true)}
            className="w-full rounded border border-dashed border-amber-300 bg-amber-50/40 px-3 py-2 text-left text-[11px] font-medium text-amber-800 hover:bg-amber-100"
            data-testid="td-notegroup-expand">
      <span className="inline-flex items-center gap-1.5">
        <StickyNote className="h-3.5 w-3.5" />
        Show {hidden.length} older internal note{hidden.length === 1 ? "" : "s"}
      </span>
    </button>
  );
}

// -------------------------------------------------------------------
// Sidebar panes
// -------------------------------------------------------------------

function SlaPane({ ticket }) {
  const fr = slaCountdown(ticket.first_response_due_at, ticket.first_response_at);
  const rs = slaCountdown(ticket.resolution_due_at,     ticket.resolved_at);
  if (!fr && !rs) return null;
  return (
    <div data-testid="td-sla-pane">
      <div className="mb-0.5 text-[10px] font-semibold uppercase text-slate-400">SLA</div>
      <div className="space-y-1.5 text-[11px]">
        {fr && (
          <div className={`flex items-center justify-between rounded border px-2 py-1 ${SLA_TONE[fr.tone]}`}>
            <span className="font-medium">First response</span>
            <span className="tabular-nums">{fr.label}</span>
          </div>
        )}
        {rs && (
          <div className={`flex items-center justify-between rounded border px-2 py-1 ${SLA_TONE[rs.tone]}`}>
            <span className="font-medium">Resolution</span>
            <span className="tabular-nums">{rs.label}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Escalation-chain sidebar — shows the requester's org, the parent
// partner (if the requester is a customer), and the Partner Success
// Manager. Data comes prefetched from GET /helpdesk/tickets/{tid} so
// no extra round-trips.
function OrgChainPane({ chain }) {
  const org     = chain?.org;
  const partner = chain?.partner;
  const psm     = chain?.psm;
  if (!org && !partner) return null;
  return (
    <div data-testid="td-org-chain">
      <div className="mb-0.5 text-[10px] font-semibold uppercase text-slate-400">Escalation chain</div>
      <div className="space-y-1 text-[11px]">
        {org && (
          <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1"
               data-testid="td-org-chain-org">
            <div className="text-[10px] uppercase text-slate-400">
              {org.type === "customer" ? "Customer" : org.type === "partner" ? "Partner" : "Organization"}
            </div>
            <div className="font-medium text-slate-700">{org.name}</div>
          </div>
        )}
        {partner && (
          <div className="rounded border border-indigo-200 bg-indigo-50 px-2 py-1"
               data-testid="td-org-chain-partner">
            <div className="text-[10px] uppercase text-indigo-500">Parent partner</div>
            <div className="font-medium text-indigo-800">{partner.name}</div>
          </div>
        )}
        {psm && (
          <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1"
               data-testid="td-org-chain-psm">
            <div className="text-[10px] uppercase text-emerald-600">Partner Success</div>
            <div className="font-medium text-emerald-800">{psm.name}</div>
            <div className="text-[10px] text-emerald-700/70">{psm.email}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// AI Triage panel — POSTs to /helpdesk/tickets/{tid}/ai-triage
// (Claude Sonnet 4.6) and renders classification + draft + next steps.
// -------------------------------------------------------------------
function AiTriagePanel({ ticket, onReplyDraft }) {
  const [busy, setBusy]   = useState(false);
  const [data, setData]   = useState(ticket?.ai_triage || null);
  const [at,   setAt]     = useState(ticket?.ai_triage_at || null);
  const [streamDraft, setStreamDraft] = useState("");

  useEffect(() => {
    setData(ticket?.ai_triage || null);
    setAt(ticket?.ai_triage_at || null);
    setStreamDraft("");
  }, [ticket?.ticket_id, ticket?.ai_triage_at]);

  const run = async () => {
    setBusy(true);
    setStreamDraft("");
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const url = `${backend}/api/helpdesk/tickets/${ticket.ticket_id}/ai-triage/stream`;
      const res = await fetch(url, { method: "GET", credentials: "include" });
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let draft = "";
      let seenMeta = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let obj;
          try { obj = JSON.parse(payload); } catch { continue; }
          if (obj.delta) {
            if (seenMeta) continue;
            draft += obj.delta;
            const marker = draft.match(/---\s*META\s*---/);
            if (marker) {
              seenMeta = true;
              draft = draft.slice(0, marker.index).trimEnd();
            }
            setStreamDraft(draft);
          } else if (obj.done) {
            setData(obj.triage);
            setAt(obj.at);
            setStreamDraft("");
          } else if (obj.error) {
            toast.error(`AI triage: ${obj.error}`);
          }
        }
      }
      toast.success("AI triage complete");
    } catch (ex) {
      const detail = ex?.message || "AI triage failed";
      toast.error(String(detail));
    } finally { setBusy(false); }
  };

  const cls = data?.classification || {};
  const nextSteps = data?.next_steps || [];
  const draft = data?.reply_draft || "";

  return (
    <div className="space-y-3" data-testid="td-ai-panel">
      <Card className="border-slate-200 bg-white p-3 text-xs">
        <div className="mb-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Claude Sonnet 4.6
          </span>
          <Button size="sm" onClick={run} disabled={busy}
                  data-testid="td-ai-run"
                  className="h-7 px-2 text-[11px]">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (data ? "Re-run" : "Run triage")}
          </Button>
        </div>
        {streamDraft && (
          <div className="mb-2 rounded-md border border-sky-200 bg-sky-50 p-2 text-slate-700 whitespace-pre-wrap"
               data-testid="td-ai-stream-draft">
            {streamDraft}
            <span className="ml-0.5 inline-block h-3 w-1 animate-pulse bg-sky-600 align-middle" />
          </div>
        )}
        {!data && !busy && !streamDraft && (
          <p className="text-slate-500">Classify this ticket, draft a reply, and suggest next steps.</p>
        )}
        {at && (
          <p className="mb-2 text-[10px] text-slate-400">Last run: {fmt(at)}</p>
        )}
        {data && (
          <>
            <div className="mb-3 grid grid-cols-2 gap-1.5">
              <TriageChip label="Category"  value={cls.category} />
              <TriageChip label="Urgency"   value={cls.urgency}   tint={cls.urgency === "urgent" ? "rose" : cls.urgency === "high" ? "amber" : "slate"} />
              <TriageChip label="Sub"       value={cls.sub_category} />
              <TriageChip label="Sentiment" value={cls.sentiment} tint={cls.sentiment === "angry" ? "rose" : cls.sentiment === "negative" ? "amber" : "emerald"} />
            </div>
            {draft && (
              <div className="mb-2">
                <div className="mb-1 text-[10px] font-semibold uppercase text-slate-500">Draft reply</div>
                <div className="rounded-md border border-sky-200 bg-sky-50 p-2 text-slate-700 whitespace-pre-wrap">{draft}</div>
                <Button size="sm" variant="outline" className="mt-1 h-7 px-2 text-[11px]"
                        onClick={() => onReplyDraft?.(draft)} data-testid="td-ai-use-draft">
                  Use draft
                </Button>
              </div>
            )}
            {nextSteps.length > 0 && (
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase text-slate-500">Next steps</div>
                <ul className="list-disc space-y-0.5 pl-4 text-slate-700">
                  {nextSteps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function TriageChip({ label, value, tint = "slate" }) {
  const tintMap = {
    slate:   "bg-slate-100 text-slate-700",
    rose:    "bg-rose-100 text-rose-800",
    amber:   "bg-amber-100 text-amber-800",
    emerald: "bg-emerald-100 text-emerald-800",
  };
  if (!value) return null;
  return (
    <div className="flex items-center gap-1 text-[10px]">
      <span className="uppercase text-slate-400">{label}</span>
      <span className={`rounded-full px-1.5 py-0.5 font-semibold ${tintMap[tint] || tintMap.slate}`}>{value}</span>
    </div>
  );
}
